import React, { forwardRef } from 'react';
import GLYPHS from './glyphs';
import './icons.css';

/**
 * One of ANNA's own icons, drawn from glyphs.js. Sizes like an icon font (1em by default)
 * and takes its colour from the text. Takes the same props react-icons did (size, color,
 * title, className, style), so the named exports below can replace react-icons one for one.
 */
const Icon = forwardRef(({ name, size = '1em', color, title, className, style, strokeWidth = 1.75, ...rest }, ref) => {
  const markup = GLYPHS[name];
  if (!markup) {
    if (process.env.NODE_ENV !== 'production') console.warn(`Icon: no glyph named "${name}"`);
    return null;
  }
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`ic ic-${name}${className ? ` ${className}` : ''}`}
      style={color ? { color, ...style } : style}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
      // The glyphs are fixed strings written in glyphs.js, never user input.
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
});
Icon.displayName = 'Icon';

const named = (name, displayName) => {
  const Named = forwardRef((props, ref) => <Icon ref={ref} name={name} {...props} />);
  Named.displayName = displayName;
  return Named;
};

export const ActivityIcon = named('activity', 'ActivityIcon');
export const AlertCircleIcon = named('alert-circle', 'AlertCircleIcon');
export const AlertTriangleIcon = named('alert-triangle', 'AlertTriangleIcon');
export const ArrowDownIcon = named('arrow-down', 'ArrowDownIcon');
export const ArrowLeftCircleIcon = named('arrow-left-circle', 'ArrowLeftCircleIcon');
export const ArrowLeftIcon = named('arrow-left', 'ArrowLeftIcon');
export const ArrowRightIcon = named('arrow-right', 'ArrowRightIcon');
export const ArrowUpCircleIcon = named('arrow-up-circle', 'ArrowUpCircleIcon');
export const ArrowUpIcon = named('arrow-up', 'ArrowUpIcon');
export const AwardIcon = named('award', 'AwardIcon');
export const BarChartIcon = named('bar-chart', 'BarChartIcon');
export const BoltIcon = named('bolt', 'BoltIcon');
export const BookIcon = named('book', 'BookIcon');
export const BookOpenIcon = named('book-open', 'BookOpenIcon');
export const BookmarkIcon = named('bookmark', 'BookmarkIcon');
export const BrainIcon = named('brain', 'BrainIcon');
export const BriefcaseIcon = named('briefcase', 'BriefcaseIcon');
export const BuildingIcon = named('building', 'BuildingIcon');
export const CalendarCheckIcon = named('calendar-check', 'CalendarCheckIcon');
export const CalendarEventIcon = named('calendar-event', 'CalendarEventIcon');
export const CalendarIcon = named('calendar', 'CalendarIcon');
export const CalendarPlusIcon = named('calendar-plus', 'CalendarPlusIcon');
export const CalendarRangeIcon = named('calendar-range', 'CalendarRangeIcon');
export const CalendarXIcon = named('calendar-x', 'CalendarXIcon');
export const ChatIcon = named('chat', 'ChatIcon');
export const ChatTextIcon = named('chat-text', 'ChatTextIcon');
export const CheckCircleIcon = named('check-circle', 'CheckCircleIcon');
export const CheckIcon = named('check', 'CheckIcon');
export const CheckSquareIcon = named('check-square', 'CheckSquareIcon');
export const ChevronDownIcon = named('chevron-down', 'ChevronDownIcon');
export const ChevronLeftIcon = named('chevron-left', 'ChevronLeftIcon');
export const ChevronRightIcon = named('chevron-right', 'ChevronRightIcon');
export const ChevronUpIcon = named('chevron-up', 'ChevronUpIcon');
export const ChildIcon = named('child', 'ChildIcon');
export const ClipboardCheckIcon = named('clipboard-check', 'ClipboardCheckIcon');
export const ClipboardIcon = named('clipboard', 'ClipboardIcon');
export const ClipboardListIcon = named('clipboard-list', 'ClipboardListIcon');
export const ClockIcon = named('clock', 'ClockIcon');
export const CloudUploadIcon = named('cloud-upload', 'CloudUploadIcon');
export const CollapseIcon = named('collapse', 'CollapseIcon');
export const CompassIcon = named('compass', 'CompassIcon');
export const CopyIcon = named('copy', 'CopyIcon');
export const CursorIcon = named('cursor', 'CursorIcon');
export const DashboardIcon = named('dashboard', 'DashboardIcon');
export const DatabaseIcon = named('database', 'DatabaseIcon');
export const DownloadIcon = named('download', 'DownloadIcon');
export const EditIcon = named('edit', 'EditIcon');
export const ExchangeIcon = named('exchange', 'ExchangeIcon');
export const ExpandIcon = named('expand', 'ExpandIcon');
export const ExternalLinkIcon = named('external-link', 'ExternalLinkIcon');
export const EyeIcon = named('eye', 'EyeIcon');
export const EyeOffIcon = named('eye-off', 'EyeOffIcon');
export const FileCheckIcon = named('file-check', 'FileCheckIcon');
export const FileIcon = named('file', 'FileIcon');
export const FileSpreadsheetIcon = named('file-spreadsheet', 'FileSpreadsheetIcon');
export const FileTextIcon = named('file-text', 'FileTextIcon');
export const FileUploadIcon = named('file-upload', 'FileUploadIcon');
export const FilterIcon = named('filter', 'FilterIcon');
export const FlowerIcon = named('flower', 'FlowerIcon');
export const FolderIcon = named('folder', 'FolderIcon');
export const FolderOpenIcon = named('folder-open', 'FolderOpenIcon');
export const FrownIcon = named('frown', 'FrownIcon');
export const GenderIcon = named('gender', 'GenderIcon');
export const GlobeIcon = named('globe', 'GlobeIcon');
export const GraduationCapIcon = named('graduation-cap', 'GraduationCapIcon');
export const GridIcon = named('grid', 'GridIcon');
export const HandHeartIcon = named('hand-heart', 'HandHeartIcon');
export const HashIcon = named('hash', 'HashIcon');
export const HeartIcon = named('heart', 'HeartIcon');
export const HeartPulseIcon = named('heart-pulse', 'HeartPulseIcon');
export const HelpCircleIcon = named('help-circle', 'HelpCircleIcon');
export const HierarchyIcon = named('hierarchy', 'HierarchyIcon');
export const HistoryIcon = named('history', 'HistoryIcon');
export const HomeIcon = named('home', 'HomeIcon');
export const HourglassIcon = named('hourglass', 'HourglassIcon');
export const IdCardIcon = named('id-card', 'IdCardIcon');
export const InfoIcon = named('info', 'InfoIcon');
export const KeyIcon = named('key', 'KeyIcon');
export const LandmarkIcon = named('landmark', 'LandmarkIcon');
export const LanguageIcon = named('language', 'LanguageIcon');
export const LayersIcon = named('layers', 'LayersIcon');
export const LightbulbIcon = named('lightbulb', 'LightbulbIcon');
export const LineChartIcon = named('line-chart', 'LineChartIcon');
export const LinkIcon = named('link', 'LinkIcon');
export const ListCheckIcon = named('list-check', 'ListCheckIcon');
export const ListIcon = named('list', 'ListIcon');
export const LockIcon = named('lock', 'LockIcon');
export const MailCheckIcon = named('mail-check', 'MailCheckIcon');
export const MailIcon = named('mail', 'MailIcon');
export const MapPinIcon = named('map-pin', 'MapPinIcon');
export const MenuIcon = named('menu', 'MenuIcon');
export const MinusCircleIcon = named('minus-circle', 'MinusCircleIcon');
export const MoneyIcon = named('money', 'MoneyIcon');
export const MoreHorizontalIcon = named('more-horizontal', 'MoreHorizontalIcon');
export const MoreVerticalIcon = named('more-vertical', 'MoreVerticalIcon');
export const PauseCircleIcon = named('pause-circle', 'PauseCircleIcon');
export const PencilIcon = named('pencil', 'PencilIcon');
export const PhoneIcon = named('phone', 'PhoneIcon');
export const PieChartIcon = named('pie-chart', 'PieChartIcon');
export const PlayCircleIcon = named('play-circle', 'PlayCircleIcon');
export const PlayIcon = named('play', 'PlayIcon');
export const PlusCircleIcon = named('plus-circle', 'PlusCircleIcon');
export const PlusIcon = named('plus', 'PlusIcon');
export const PowerIcon = named('power', 'PowerIcon');
export const PuzzleIcon = named('puzzle', 'PuzzleIcon');
export const RefreshIcon = named('refresh', 'RefreshIcon');
export const RocketIcon = named('rocket', 'RocketIcon');
export const RunningIcon = named('running', 'RunningIcon');
export const SaveIcon = named('save', 'SaveIcon');
export const ScaleIcon = named('scale', 'ScaleIcon');
export const SearchIcon = named('search', 'SearchIcon');
export const SendIcon = named('send', 'SendIcon');
export const SettingsIcon = named('settings', 'SettingsIcon');
export const ShieldCheckIcon = named('shield-check', 'ShieldCheckIcon');
export const ShieldIcon = named('shield', 'ShieldIcon');
export const ShieldLockIcon = named('shield-lock', 'ShieldLockIcon');
export const SignInIcon = named('sign-in', 'SignInIcon');
export const SignOutIcon = named('sign-out', 'SignOutIcon');
export const SlidersIcon = named('sliders', 'SlidersIcon');
export const SmileIcon = named('smile', 'SmileIcon');
export const SortIcon = named('sort', 'SortIcon');
export const SparkIcon = named('spark', 'SparkIcon');
export const SparklesIcon = named('sparkles', 'SparklesIcon');
export const StarIcon = named('star', 'StarIcon');
export const StickyNoteIcon = named('sticky-note', 'StickyNoteIcon');
export const StopwatchIcon = named('stopwatch', 'StopwatchIcon');
export const StudentIcon = named('student', 'StudentIcon');
export const SyncIcon = named('sync', 'SyncIcon');
export const TableIcon = named('table', 'TableIcon');
export const TagIcon = named('tag', 'TagIcon');
export const TargetIcon = named('target', 'TargetIcon');
export const TeacherIcon = named('teacher', 'TeacherIcon');
export const TextLeftIcon = named('text-left', 'TextLeftIcon');
export const TrashIcon = named('trash', 'TrashIcon');
export const UndoIcon = named('undo', 'UndoIcon');
export const UploadIcon = named('upload', 'UploadIcon');
export const UserCheckIcon = named('user-check', 'UserCheckIcon');
export const UserCircleIcon = named('user-circle', 'UserCircleIcon');
export const UserEditIcon = named('user-edit', 'UserEditIcon');
export const UserIcon = named('user', 'UserIcon');
export const UserMinusIcon = named('user-minus', 'UserMinusIcon');
export const UserPlusIcon = named('user-plus', 'UserPlusIcon');
export const UserSettingsIcon = named('user-settings', 'UserSettingsIcon');
export const UserXIcon = named('user-x', 'UserXIcon');
export const UsersIcon = named('users', 'UsersIcon');
export const VideoIcon = named('video', 'VideoIcon');
export const WifiOffIcon = named('wifi-off', 'WifiOffIcon');
export const XCircleIcon = named('x-circle', 'XCircleIcon');
export const XIcon = named('x', 'XIcon');
export const ZoomInIcon = named('zoom-in', 'ZoomInIcon');
export const ZoomOutIcon = named('zoom-out', 'ZoomOutIcon');

export default Icon;
